// ============================================================
// Zentryx Dominó - Event Bus
// Typed pub/sub system for decoupled communication
// Supports optional context binding (Phaser-style)
// ============================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventCallback = (...args: any[]) => void;

interface ListenerEntry {
  callback: EventCallback;
  context?: unknown;
}

class EventBusClass {
  private listeners: Map<string, ListenerEntry[]> = new Map();

  on(event: string, callback: EventCallback, context?: unknown): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push({ callback, context });
    return this;
  }

  off(event: string, callback: EventCallback, _context?: unknown): this {
    const entries = this.listeners.get(event);
    if (entries) {
      const idx = entries.findIndex((e) => e.callback === callback);
      if (idx !== -1) entries.splice(idx, 1);
    }
    return this;
  }

  once(event: string, callback: EventCallback, context?: unknown): this {
    const wrapper: EventCallback = (...args) => {
      callback.apply(context, args);
      this.off(event, wrapper);
    };
    this.listeners.get(event) || this.listeners.set(event, []);
    this.listeners.get(event)!.push({ callback: wrapper, context });
    return this;
  }

  emit(event: string, ...args: unknown[]): this {
    const entries = this.listeners.get(event);
    if (entries) {
      // Iterate over a copy to allow listeners to remove themselves
      [...entries].forEach((entry) => {
        try {
          entry.callback.apply(entry.context, args);
        } catch (e) {
          console.error(`[EventBus] Error in listener for "${event}":`, e);
        }
      });
    }
    return this;
  }

  removeAllListeners(event?: string): this {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
    return this;
  }
}

/** Singleton event bus for game-wide communication */
export const EventBus = new EventBusClass();
